import { PropsWithChildren } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { Close } from '@styled-icons/material'
import { useClickOutsideElement } from '@oneblink/apps-react'
import { motion, AnimatePresence } from 'motion/react'

export interface DialogProps {
  onClose?: () => void
  isOpen: boolean
}

export const DialogHeading = styled.div`
  display: flex;
  margin: 0;
  padding-bottom: ${({ theme }) => theme.unit / 2}px;
  margin-bottom: 1rem;
`
DialogHeading.displayName = 'DialogHeading'

export const DialogHeadingText = styled.span`
  display: flex;
  flex-grow: 1;
  font-weight: 600;
  font-size: 2rem;
`
DialogHeadingText.displayName = 'DialogHeadingText'

type Props = PropsWithChildren<DialogProps>

export const CloseIcon = styled(Close)`
  display: flex;
  height: 2rem;
  cursor: pointer;
`
CloseIcon.displayName = 'CloseIcon'

const DialogContainer = styled.div<DialogProps>`
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100vh;
  max-height: 100vh;
  overflow: auto;
  background-color: rgba(0, 38, 100, 0.9);
  z-index: 1000000;
  display: flex;
  justify-content: center;
  align-items: center;
`

DialogContainer.displayName = 'DialogContainer'

type DialogContentProps = { compact?: boolean }

const DialogContent = styled.div<DialogContentProps>`
  background-color: ${({ theme }) => theme.palette.nswPaletteOffWhite};
  width: ${({ compact }) => !compact && '80%'};
  max-height: calc(100vh - 15%);
  @media (${({ theme }) => theme.screenSizes.xs}) {
    width: 100%;
  }
  display: flex;
  flex: 0 0 auto;
  border-radius: 4px;
  flex-direction: column;
  padding: ${({ theme }) => theme.unit * 2}px;
  overflow-y: auto;
`

DialogContent.displayName = 'DialogContent'

export default function Dialog({
  children,
  onClose,
  compact,
  ...props
}: Props & DialogContentProps) {
  const contentRef = React.useRef(null)

  const onClickOutside = React.useCallback(() => {
    if (onClose) {
      onClose()
    }
  }, [onClose])

  useClickOutsideElement(contentRef, onClickOutside)

  return (
    <AnimatePresence>
      {props.isOpen && (
        <motion.div
          initial={{ opacity: 0, zIndex: 10000 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <DialogContainer {...props}>
            <DialogContent ref={contentRef} compact={compact}>
              {children}
            </DialogContent>
          </DialogContainer>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
